/**
 * WebsiteContentPanel.js — Phases B + C
 * AD-facing UI to manage WordPress pages and posts (news) directly
 * from inside OL, without ever logging into WP-admin.
 *
 * Single-purpose per launcher card (no internal tabs):
 *   • kind="pages" → "Edit a page": grouped, searchable page browser
 *     organised by the real WP parent/child hierarchy (Teams, Community…)
 *   • kind="posts" → "Post news": dated article list
 *
 * Pattern: on mount, GET the list (which triggers an initial sync
 * from the bridge if Mongo has zero rows for this org). Click any
 * row → opens an edit drawer (TipTap body editor). Save → POST/PATCH;
 * a green check appears when the bridge confirms wp_pushed=true.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';
import WpBodyEditor from './WpBodyEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Editor = ({ kind, row, orgId, onClose, onSaved }) => {
  const [title, setTitle] = useState(row?.title || '');
  const [content, setContent] = useState(row?.content || '');
  const [excerpt, setExcerpt] = useState(row?.excerpt || '');
  const [slug, setSlug] = useState(row?.slug || '');
  const [status, setStatus] = useState(row?.status || 'publish');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // Destinations (News only). Both are persistent STATE: ticked means "this
  // story is on that surface". Un-ticking removes it there. Saving a story
  // that is already on Facebook does not post it twice.
  const [destWp, setDestWp] = useState(row?.destinations?.wp !== false);
  const [destFb, setDestFb] = useState(!!row?.fb_post_id);
  const [fbCfg, setFbCfg] = useState(null);

  useEffect(() => {
    if (kind !== 'posts' || !orgId) return;
    axios.get(`${API}/facebook/${orgId}/config`, { withCredentials: true })
      .then(({ data }) => setFbCfg(data))
      .catch(() => setFbCfg({ connected: false }));
  }, [kind, orgId]);

  const save = async () => {
    setSaving(true);
    try {
      // News: no slug field — WordPress always generates it from the title.
      const body = kind === 'posts' ? { title, content, status } : { title, content, slug, status };
      if (kind === 'posts') {
        body.excerpt = excerpt;
        // Facebook-only stories are never pushed to WordPress, so they never
        // become a website article (AJ, 2026-10-11).
        body.destinations = { wp: destWp, facebook: destFb };
      }
      const url = row.id
        ? `${API}/wp-${kind}/${orgId}/${row.id}`
        : `${API}/wp-${kind}/${orgId}`;
      const method = row.id ? 'patch' : 'post';
      const { data } = await axios({ method, url, data: body, withCredentials: true });

      // The story is saved. Facebook is attempted AFTER that and can never
      // undo it — a Meta failure is reported but the story still exists.
      let fbNote = null;
      const alreadyOnFb = !!(data?.post?.fb_post_id);
      if (kind === 'posts' && destFb && data?.ok && !alreadyOnFb) {
        const postId = data?.post?.id || row.id;
        try {
          const { data: fb } = await axios.post(
            `${API}/facebook/${orgId}/publish`, { post_id: postId },
            { withCredentials: true });
          fbNote = { ok: true, text: `Posted to ${fb.page_name || 'Facebook'} (${fb.kind} post).` };
        } catch (e) {
          fbNote = { ok: false, text: e.response?.data?.detail || 'Facebook publish failed.' };
        }
      } else if (data?.removed_from_fb === true) {
        fbNote = { ok: true, text: 'Removed from the Facebook Page.' };
      } else if (data?.removed_from_fb === false) {
        fbNote = { ok: false, text: 'Could not remove the post from Facebook — check Tools → Facebook.' };
      }
      setResult({ ...data, fbNote });
      if (data.ok) onSaved && onSaved();
    } catch (e) {
      setResult({ ok: false, wp_error: { message: e.message } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="wp-content-editor" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {/* Explicit dark text + light form controls: in OL dark mode the body text is
          near-white (#f8fafc) and this white dialog used to inherit it — light grey on
          white, unreadable (bug report 2026-10-11). */}
      <div className="bg-white text-gray-900 [color-scheme:light] rounded-lg w-[800px] max-h-[90vh] overflow-y-auto p-6 shadow-xl">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold">
            {row.id ? 'Edit' : 'New'} {kind === 'pages' ? 'Page' : 'News Post'}
          </h2>
          <button data-testid="wp-editor-close" onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
        </div>
        <div className={`mb-4 px-3 py-2 rounded text-xs font-medium ${
          kind === 'pages' ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                           : 'bg-amber-50 text-amber-800 border border-amber-200'
        }`} data-testid="wp-editor-kind-hint">
          {kind === 'pages'
            ? '📄 You are editing a WordPress PAGE — a permanent URL on your site.'
            : '📰 You are editing a NEWS POST — it appears in the rolling /news feed.'}
        </div>

        <label className="block text-sm font-medium mb-1">Title</label>
        <input
          data-testid="wp-editor-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border rounded px-3 py-2 mb-3"
        />

        {kind !== 'posts' && (
          <>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <input
              data-testid="wp-editor-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="auto-generated if blank"
              className="w-full border rounded px-3 py-2 mb-3"
            />
          </>
        )}

        {kind === 'posts' && (
          <>
            <label className="block text-sm font-medium mb-1">Excerpt</label>
            <textarea
              data-testid="wp-editor-excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={2}
              className="w-full border rounded px-3 py-2 mb-3"
            />
          </>
        )}

        <label className="block text-sm font-medium mb-1">Content</label>
        <WpBodyEditor value={content} onChange={setContent} testIdPrefix="wp-editor-body" />
        <div className="mb-3" />
        {kind === 'posts' && (
          <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3" data-testid="wp-editor-destinations">
            <div className="text-sm font-medium mb-2">Publish to</div>
            <label className="flex items-start gap-2 mb-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="wp-editor-dest-wp"
                checked={destWp}
                onChange={(e) => setDestWp(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong>Website</strong> — appears in the rolling /news feed.
                {!destWp && (
                  <em className="block text-xs text-amber-700 mt-0.5">
                    {row.wp_id
                      ? 'Unticking this REMOVES the article from the website when you save.'
                      : 'Unticked: this story will NOT become a website article.'}
                  </em>
                )}
              </span>
            </label>
            <label className={`flex items-start gap-2 ${fbCfg?.connected ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                data-testid="wp-editor-dest-fb"
                checked={destFb}
                disabled={!fbCfg?.connected}
                onChange={(e) => setDestFb(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong>Facebook</strong>
                {fbCfg?.connected
                  ? <> — posts to {fbCfg.page_name || 'the connected Page'} when you save.</>
                  : <> — not connected. Set it up in Tools → Facebook.</>}
                {row.fb_post_id && !destFb && (
                  <em className="block text-xs text-amber-700 mt-0.5">
                    Unticking this REMOVES the post from the Page when you save.
                  </em>
                )}
                {row.fb_post_id && destFb && (
                  <em className="block text-xs text-gray-600 mt-0.5">
                    Already on the Page — saving again won't post a duplicate.
                  </em>
                )}
              </span>
            </label>
            {!destWp && !destFb && (
              <p className="text-xs text-amber-700 mt-2">
                Nothing selected — the story is saved in OmniLeagues only.
              </p>
            )}
          </div>
        )}


        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          data-testid="wp-editor-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full border rounded px-3 py-2 mb-4"
        >
          <option value="publish">Published</option>
          <option value="draft">Draft</option>
          <option value="private">Private</option>
        </select>

        {result?.fbNote && (
        <div
          data-testid="wp-editor-fb-note"
          className={`mb-3 px-3 py-2 rounded text-xs font-medium border ${
            result.fbNote.ok
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-amber-50 text-amber-800 border-amber-200'}`}
        >
          {result.fbNote.ok ? '✅ ' : '⚠ '}{result.fbNote.text}
        </div>
      )}
      {result && (
          <div data-testid="wp-editor-result"
               className={`p-3 mb-3 rounded text-sm ${result.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {result.ok
              ? (result.queued === false
                  ? (result.removed_from_wp
                      ? '✓ Saved. Removed from the website.'
                      : '✓ Saved. Not published to the website (Website was unticked).')
                  : `✓ Saved + published to WP (wp_id=${(result.page || result.post)?.wp_id || '?'})`)
              : `✗ Save failed: ${JSON.stringify(result.wp_error || result)}`}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button data-testid="wp-editor-cancel" onClick={onClose}
                  className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
          <button data-testid="wp-editor-save" onClick={save} disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {saving
              ? 'Saving…'
              : (kind === 'posts' && !destWp
                  ? (row.id ? 'Save' : 'Create')
                  : (row.id ? 'Save & Publish' : 'Create & Publish'))}
          </button>
        </div>
      </div>
    </div>
  );
};


const decodeEntities = (s = '') =>
  s.replace(/&amp;/g, '&').replace(/&#8217;/g, '\u2019').replace(/&#8211;/g, '\u2013')
   .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');

/* One page row inside a section card. */
const PageRow = ({ r, path, siteUrl, onEdit, onDelete }) => (
  <div
    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-indigo-50 group cursor-pointer"
    data-testid={`wp-content-row-${r.id || r.wp_id}`}
    onClick={() => onEdit(r)}
  >
    <span className={`h-2 w-2 rounded-full shrink-0 ${r.status === 'publish' ? 'bg-green-500' : 'bg-gray-300'}`}
          title={r.status} />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate">{decodeEntities(r.title) || '(untitled)'}</div>
      <div className="text-[11px] text-gray-400 truncate">{path}</div>
    </div>
    {r.wp_sync_state === 'error' && <span className="text-[11px] text-red-600 shrink-0">✗ sync error</span>}
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
         onClick={(e) => e.stopPropagation()}>
      {siteUrl && (
        <a href={`${siteUrl}${path}`} target="_blank" rel="noreferrer"
           className="px-2 py-1 text-xs border rounded hover:bg-white text-gray-600"
           data-testid={`wp-content-view-${r.id || r.wp_id}`}>View ↗</a>
      )}
      <button data-testid={`wp-content-edit-${r.id || r.wp_id}`} onClick={() => onEdit(r)}
              className="px-2 py-1 text-xs border rounded hover:bg-white">Edit</button>
      <button data-testid={`wp-content-delete-${r.id || r.wp_id}`} onClick={() => onDelete(r)}
              className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Del</button>
    </div>
  </div>
);

export default function WebsiteContentPanel({ orgId, kind = 'pages' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncErr, setSyncErr] = useState(null);
  const [search, setSearch] = useState('');
  const [siteUrl, setSiteUrl] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/wp-${kind}/${orgId}?refresh=1`,
        { withCredentials: true });
      setRows(data[kind] || []);
      setSyncErr(data.bridge_sync_error || null);
    } catch (e) {
      setRows([]);
      setSyncErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, kind]);

  useEffect(() => { load(); }, [load]);

  // News saves/deletes publish via a queue (~3 s): re-check once so the row flips
  // from "publishing…" to "synced". Cleared on unmount.
  const recheckTimer = useRef(null);
  const recheckSoon = useCallback(() => {
    clearTimeout(recheckTimer.current);
    recheckTimer.current = setTimeout(load, 4000);
  }, [load]);
  useEffect(() => () => clearTimeout(recheckTimer.current), []);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/website-overview/${orgId}`, { withCredentials: true })
      .then(({ data }) => setSiteUrl(data?.bridge?.site_url || ''))
      .catch(() => {});
  }, [orgId]);

  const del = async (row) => {
    const where = kind === 'posts'
      ? [row.wp_id ? 'the WordPress site' : null, row.fb_post_id ? 'the Facebook Page' : null]
          .filter(Boolean).join(' and ')
      : null;
    const ok = window.confirm(
      kind === 'posts'
        ? `Delete "${row.title || '(untitled)'}"?${where ? ` It is also permanently deleted from ${where}.` : ''}`
        : `Delete "${row.title || '(untitled)'}" — this trashes the page on WordPress too.`,
    );
    if (!ok) return;
    if (kind === 'posts') {
      // News deletes are queued and removed from WordPress within seconds (permanently).
      await axios.delete(`${API}/wp-${kind}/${orgId}/${row.id}`, { withCredentials: true });
      await load();
      recheckSoon();
      return;
    }
    const force = window.confirm('OK = permanently delete · Cancel = move to Trash (recoverable from wp-admin Trash)');
    await axios.delete(`${API}/wp-${kind}/${orgId}/${row.id}?force=${force ? 1 : 0}`, { withCredentials: true });
    await load();
  };

  const kindLabel = kind === 'pages' ? 'Page' : 'News Post';

  /* ── Page hierarchy (kind="pages") ───────────────────────────────────
     Mirrors the real WP parent/child tree so the 38 pages read like the
     site's own structure: Teams (13), Community (6), Members… plus a
     "General pages" bucket for standalone top-level pages. */
  const { sections, standalone, byId } = React.useMemo(() => {
    const norm = rows.map((r) => ({
      ...r, _wp: String(r.wp_id ?? ''), _parent: String(r.parent ?? '0'),
    }));
    const byId = {};
    norm.forEach((r) => { if (r._wp) byId[r._wp] = r; });
    const children = {};
    norm.forEach((r) => {
      if (r._parent !== '0' && byId[r._parent]) {
        (children[r._parent] = children[r._parent] || []).push(r);
      }
    });
    Object.values(children).forEach((list) =>
      list.sort((a, b) => (a.title || '').localeCompare(b.title || '')));
    const sections = norm
      .filter((r) => children[r._wp])
      .sort((a, b) => (children[b._wp].length - children[a._wp].length))
      .map((parent) => ({ parent, children: children[parent._wp] }));
    const standalone = norm
      .filter((r) => (r._parent === '0' || !byId[r._parent]) && !children[r._wp])
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return { sections, standalone, byId };
  }, [rows]);

  const pathOf = useCallback((r, depth = 0) => {
    if (!r) return '';
    if (depth > 4 || r._parent === '0' || !byId[r._parent]) return `/${r.slug}`;
    return `${pathOf(byId[r._parent], depth + 1)}/${r.slug}`;
  }, [byId]);

  const matches = (r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.title || '').toLowerCase().includes(q) || (r.slug || '').toLowerCase().includes(q);
  };

  return (
    <div data-testid="website-content-panel" className="p-6 space-y-4">
      <LiveSiteHeader orgId={orgId} surfaceLabel={kind === 'pages' ? 'Pages' : 'News (Posts)'}
                       wpPath={kind === 'pages' ? '' : '/news'} />
      <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">{kind === 'pages' ? 'Edit a Page' : 'News'}</h1>
          <div className="text-xs text-gray-500 mt-0.5" data-testid="wp-content-tab-hint">
            {kind === 'pages'
              ? `${rows.length} pages, organised the way your site is — pick one to edit. Changes publish to WordPress on save.`
              : 'Dated articles on the /news feed. Saves and deletes reach WordPress within seconds; posts written in wp-admin show up here automatically.'}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="wp-content-refresh"
            onClick={() => load()}
            className="px-3 py-2 border rounded text-sm hover:bg-gray-50"
          >↻ Refresh from WP</button>
          <button
            data-testid="wp-content-new"
            onClick={() => setEditing({})}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >+ New {kindLabel}</button>
        </div>
      </div>

      {syncErr && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="wp-content-sync-err">
          {/404/.test(syncErr)
            ? 'Your WordPress site looks offline (Local site stopped or Live Link expired). Showing OL\'s saved copy — it will re-sync when the site is back.'
            : `Bridge sync warning: ${syncErr}. Showing cached data.`}
        </div>
      )}

      {kind === 'pages' && (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍  Search pages by name or URL…"
          className="w-full md:w-96 border rounded-lg px-3 py-2 text-sm"
          data-testid="wp-content-search"
        />
      )}

      {loading && <div className="text-gray-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-gray-500 text-sm py-8 text-center" data-testid="wp-content-empty">
          No {kindLabel.toLowerCase()}s yet. {kind === 'pages'
            ? 'Create one — it will publish to WordPress immediately.'
            : 'Create one — it will appear on /news immediately.'}
        </div>
      )}

      {/* ── Pages: grouped browser ── */}
      {!loading && kind === 'pages' && rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="wp-content-sections">
          {sections.map(({ parent, children }) => {
            const kids = children.filter(matches);
            const parentMatch = matches(parent);
            if (!kids.length && !parentMatch) return null;
            return (
              <div key={parent._wp} className="border rounded-xl bg-white overflow-hidden"
                   data-testid={`wp-content-section-${parent.slug}`}>
                <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50/70 border-b">
                  <div>
                    <div className="font-semibold text-sm text-indigo-900">
                      {decodeEntities(parent.title)}
                      <span className="ml-2 text-[11px] font-normal text-indigo-400">
                        {children.length} page{children.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="text-[11px] text-indigo-400">{pathOf(parent)}</div>
                  </div>
                  <button onClick={() => setEditing(parent)}
                          className="px-2 py-1 text-xs border border-indigo-200 rounded hover:bg-white text-indigo-700"
                          data-testid={`wp-content-edit-section-${parent.slug}`}>
                    Edit section page
                  </button>
                </div>
                <div className="p-1.5">
                  {kids.map((r) => (
                    <PageRow key={r.id || r._wp} r={r} path={pathOf(r)} siteUrl={siteUrl}
                             onEdit={setEditing} onDelete={del} />
                  ))}
                  {!kids.length && (
                    <div className="px-3 py-2 text-xs text-gray-400">No matching pages in this section</div>
                  )}
                </div>
              </div>
            );
          })}

          {standalone.filter(matches).length > 0 && (
            <div className="border rounded-xl bg-white overflow-hidden" data-testid="wp-content-section-general">
              <div className="px-4 py-2.5 bg-gray-50 border-b">
                <div className="font-semibold text-sm text-gray-700">
                  General pages
                  <span className="ml-2 text-[11px] font-normal text-gray-400">
                    {standalone.length} page{standalone.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="text-[11px] text-gray-400">Top-level pages without sub-pages</div>
              </div>
              <div className="p-1.5">
                {standalone.filter(matches).map((r) => (
                  <PageRow key={r.id || r._wp} r={r} path={pathOf(r)} siteUrl={siteUrl}
                           onEdit={setEditing} onDelete={del} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── News: dated article table ── */}
      {!loading && kind === 'posts' && rows.length > 0 && (
        <table className="w-full text-sm" data-testid="wp-content-table">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Sync</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id || r.wp_id} className="border-b hover:bg-amber-50"
                  data-testid={`wp-content-row-${r.id || r.wp_id}`}>
                <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => setEditing(r)}>
                  <span className="inline-block mr-2 px-1.5 py-0.5 rounded text-[10px] font-semibold align-middle bg-amber-100 text-amber-700">NEWS</span>
                  {decodeEntities(r.title) || '(untitled)'}
                </td>
                <td className="px-3 py-2 text-gray-600">{r.slug}</td>
                <td className="px-3 py-2 text-xs"><span className={`px-2 py-0.5 rounded ${r.status === 'publish' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{r.status || '—'}</span></td>
                <td className="px-3 py-2">
                  {r.wp_sync_state === 'synced' && <span className="text-green-600">✓ synced</span>}
                  {r.wp_sync_state === 'pending' && <span className="text-amber-600" data-testid={`wp-content-pending-${r.id || r.wp_id}`}>⏳ publishing…</span>}
                  {r.wp_sync_state === 'error' && <span className="text-red-600">✗ error</span>}
                  {!r.wp_sync_state && <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-gray-500">{(r.wp_modified_at || r.updated_at || r.created_at || r.synced_at || '').slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`wp-content-edit-${r.id || r.wp_id}`} onClick={() => setEditing(r)}
                          className="px-2 py-1 text-xs border rounded hover:bg-gray-50 mr-1">Edit</button>
                  <button data-testid={`wp-content-delete-${r.id || r.wp_id}`} onClick={() => del(r)}
                          className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <Editor
          kind={kind}
          row={editing}
          orgId={orgId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); if (kind === 'posts') recheckSoon(); }}
        />
      )}
    </div>
  );
}
